'use client'

import { useAtomValue } from 'jotai'
import { useMemo } from 'react'

import { resolveAccount } from '@/lib/utils/resolve-account'
import { useUserAccounts } from '@/service/account.hook'
import {
  defaultOrderAccountByTradeVenueAtom,
  lastOrderAccountByTradeVenueAtom,
} from '@/state/order'
import type { TradeVenue } from '@/types/generated/common/types'

/** Account for a new order on `tradeVenue` from a card-bound surface. Priority: the last
 * account explicitly picked in the Order Form for this venue → `cardAccountId` → the venue's
 * default. Pass the card's explicit/tab-pinned trading selection as `cardAccountId`
 * (`useCardAccountContext().cardTradingSelection?.accountId`), not its derived default. */
export function usePreferredOrderAccountId(
  tradeVenue: TradeVenue | undefined,
  cardAccountId?: string
): string | undefined {
  const { data } = useUserAccounts()
  const lastAccountByTradeVenue = useAtomValue(lastOrderAccountByTradeVenueAtom)
  const defaultAccountByTradeVenue = useAtomValue(defaultOrderAccountByTradeVenueAtom)

  return useMemo(() => {
    const resolution = resolveAccount({
      accounts: data?.accounts ?? [],
      purpose: 'trade',
      intent: 'new',
      tradeVenue,
      userDefaultAccountId:
        tradeVenue === undefined ? undefined : defaultAccountByTradeVenue[tradeVenue],
      rememberedAccountId:
        tradeVenue === undefined ? undefined : lastAccountByTradeVenue[tradeVenue],
      cardAccountId,
    })
    return resolution.status === 'resolved' ? resolution.accountId : undefined
  }, [
    cardAccountId,
    data?.accounts,
    defaultAccountByTradeVenue,
    lastAccountByTradeVenue,
    tradeVenue,
  ])
}
